import { useRef, useState } from "react"

import { Box, Fade, Slide, Stack, Typography } from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"
import { InView } from "react-intersection-observer"

import { gsap } from "@ggs/ui/local/gsap"
import type { ButtonProps, FC, FCWC } from ".."
import { Button, Container, Link, SvgIcon } from ".."
import useIsomorphicLayoutEffect from "../../helpers/useIsomorphicLayoutEffect"

type AwardsListProps = {
  title: string
  subtitle: string
  items: AwardsListItemType[]
}

type AwardsListItemType = {
  title: string
  year: string
  award: string
  link?: { title?: string; uri: string }
}

const AwardsList: FC<AwardsListProps> = ({ title, subtitle, items }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [displayedItems, setDisplayedItems] = useState(items?.slice(0, 20))
  const [displayAllAwards, setDisplayAllAwards] = useState(false)

  const getCtaLabel = () => {
    if (items?.length > 20) {
      const startYear = items[20].year
      const endYear = items[items.length - 1].year
      return displayAllAwards ? "View Less" : `View Archive [${startYear} - ${endYear}]`
    }
  }

  const onToggleDisplayMore = () => {
    if (displayAllAwards) setDisplayedItems(items?.slice(0, 20))
    else setDisplayedItems(items)
    setDisplayAllAwards(!displayAllAwards)
  }
  return (
    <AwardsListSection>
      <Container maxWidth={1560}>
        <AwardsListHeader
          alignContent="center"
          textAlign={{ xs: "start", md: "center" }}
          rowGap={{ xs: 5, md: 10 }}
          position="relative"
        >
          <Typography variant="body.lg" component="h3" color={(theme) => theme.palette["secondary.800"].main}>
            {subtitle}
          </Typography>
          <Typography variant="heading.3xl" component="h2" color={(theme) => theme.palette["secondary.800"].main}>
            {title}
          </Typography>
        </AwardsListHeader>
        <Box
          width="100%"
          borderBottom={(theme) => `1px solid ${theme.palette["neutral.200"].main}`}
          sx={{ overflowX: "auto", overflowY: "hidden", whiteSpace: "nowrap" }}
          mb={{ xs: 20, md: 45 }}
        >
          <Box ref={containerRef}>
            {displayedItems?.map((item, i) => (
              <InView key={i} triggerOnce>
                {({ inView, ref }) => (
                  <Box ref={ref}>
                    <Slide
                      direction="up"
                      in={inView}
                      container={containerRef.current}
                      timeout={100 * (i + 1)}
                    >
                      <Box>
                        <Fade in={inView} timeout={100 * (i + 1)}>
                          <Box>
                            <AwardsListItem {...item} />
                          </Box>
                        </Fade>
                      </Box>
                    </Slide>
                  </Box>
                )}
              </InView>
            ))}
          </Box>
        </Box>
        {items?.length > 20 && (
          <Box>
            <AwardsShowMoreCTA onClick={onToggleDisplayMore}>{getCtaLabel()}</AwardsShowMoreCTA>
          </Box>
        )}
      </Container>
    </AwardsListSection>
  )
}

export default AwardsList

/** A sub-component for the Awards list container */
const AwardsListSection: FCWC = ({ children }) => {
  const theme = useTheme()
  const mainRef = useRef<HTMLDivElement>(null)

  useIsomorphicLayoutEffect(() => {
    if (!mainRef.current) return
    const ctx = gsap.context(() => {
      const main = document.querySelector("main")

      gsap.to(main, {
        backgroundColor: theme.palette["secondary.200"].main,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "top 75%",
          end: "bottom 90%",
          // scrub: true,
          // markers: true,
          toggleActions: "play reverse play reverse",
          invalidateOnRefresh: true,
        },
      })
    }, mainRef)
    return () => ctx.revert()
  }, [mainRef])

  return (
    <Box ref={mainRef} py={{ xs: 20, md: 55 }} position="relative">
      {children}
    </Box>
  )
}

/** A sub-component for the Awards list header */
const AwardsListHeader = styled(Stack)(
  ({ theme }) => `
  margin: 0 auto;
  max-width: 1060px;
  margin-bottom: ${theme.spacing(45)};
  width: 100%;
  ${theme.breakpoints.down("md")} {
    margin-bottom: ${theme.spacing(20)};
  }
`
)

/** A sub-component for the Awards list item */
const AwardsListItem: FC<AwardsListItemType> = ({ title, year, award, link }) => {
  const isLink = link?.title && link?.uri
  return (
    <Box
      position="relative"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      gap={10}
      px={5}
      minHeight={{ xs: 68, md: 90 }}
      minWidth="900px"
      borderTop={(theme) => `1px solid ${theme.palette["neutral.200"].main}`}
    >
      {isLink && <Link type="overlay" href={link?.uri} aria-label={title} title={title} />}
      <Box position="relative" display="flex" gap={10} alignItems="center">
        <Typography
          variant="body.sm"
          sx={{ color: (theme) => theme.palette["secondary.800"].main }}
        >
          {year}
        </Typography>
        <Typography
          variant="body.md"
          component="h2"
          sx={{ color: (theme) => theme.palette["secondary.800"].main }}
        >
          {title}
        </Typography>
      </Box>
      <Box display="flex" gap={10}>
        <Typography
          variant="body.sm"
          sx={{ color: (theme) => theme.palette["secondary.800"].main }}
        >
          {award}
        </Typography>
        {isLink && (
          <Box>
            <SvgIcon variant="arrow" size={15} sx={{ transform: "rotate(180deg)" }} />
          </Box>
        )}
      </Box>
    </Box>
  )
}

/** A sub-component for the Awards list expand button. */
const AwardsShowMoreCTA: FC<ButtonProps> = (props) => (
  <Box textAlign="center">
    <Button variant="secondary" {...props} />
  </Box>
)
